import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { fetchProducts, fetchCategories, fetchBrands } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SlidersHorizontal, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const ShopPage = () => {
  const [params, setParams] = useSearchParams();
  const category = params.get('category') || '';
  const brand = params.get('brand') || '';
  const q = params.get('q') || '';
  const [sort, setSort] = useState('popularity');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchCategories(), fetchBrands()])
      .then(([c, b]) => { setCategories(c); setBrands(b); })
      .catch(console.error);
  }, []);

  useEffect(() => {
    setLoading(true);
    const queryParams = {};
    if (category) queryParams.category = category;
    if (brand) queryParams.brand = brand;
    if (q) queryParams.q = q;
    if (sort) queryParams.sort = sort;
    fetchProducts(queryParams)
      .then(setProducts)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [category, brand, q, sort]);

  const clear = () => setParams({});
  const activeCategory = useMemo(() => categories.find(c => c.id === category), [categories, category]);
  const activeBrand = useMemo(() => brands.find(b => b.id === brand), [brands, brand]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-black tracking-tight uppercase">
          {activeCategory ? activeCategory.name : activeBrand ? activeBrand.name : 'Shop All Products'}
        </h1>
        <p className="text-neutral-500 mt-1">Browse our complete collection of health and performance supplements.</p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="text-sm font-semibold flex items-center gap-2"><SlidersHorizontal className="w-4 h-4" /> Filters:</span>
        <Select value={category || 'all'} onValueChange={(v) => { v === 'all' ? params.delete('category') : params.set('category', v); setParams(params); }}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={brand || 'all'} onValueChange={(v) => { v === 'all' ? params.delete('brand') : params.set('brand', v); setParams(params); }}>
          <SelectTrigger className="w-44"><SelectValue placeholder="Brand" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Brands</SelectItem>
            {brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="popularity">Sort by Popularity</SelectItem>
            <SelectItem value="price-asc">Price: Low to High</SelectItem>
            <SelectItem value="price-desc">Price: High to Low</SelectItem>
            <SelectItem value="name-asc">Name: A to Z</SelectItem>
            <SelectItem value="name-desc">Name: Z to A</SelectItem>
          </SelectContent>
        </Select>
        {(category || brand || q) && <Button variant="outline" size="sm" onClick={clear}>Clear filters</Button>}
        <span className="ml-auto text-sm text-neutral-500">{products.length} products</span>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-neutral-400" /></div>
      ) : products.length === 0 ? (
        <div className="text-center py-20 border rounded-lg">
          <p className="text-neutral-500">No products found.</p>
          <Link to="/shop" className="text-[#0a0a0a] font-semibold underline mt-2 inline-block">View all products</Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {products.map(p => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
    </div>
  );
};

export default ShopPage;
